import React, { useEffect, useCallback } from "react";
import { Control, useFieldArray, Controller, useWatch } from "react-hook-form";
import { Box, Button, Input, Text, Tooltip } from "rizzui";
import { PiTrashBold, PiPlusBold, PiInfoBold } from "react-icons/pi";
import { FormData } from "../types";

interface BSDSectionProps {
  control: Control<FormData>;
}

/**
 * Calculate base amount for a tier based on previous tiers
 * Formula: BaseAmount[n] = BaseAmount[n-1] + (MaxPrice[n-1] - MaxPrice[n-2]) × Rate[n-1]
 */
const calculateBaseAmounts = (
  tiers: Array<{ maxPrice: number; rate: number; baseAmount: number }>,
): number[] => {
  if (!tiers || tiers.length === 0) return [];

  const baseAmounts: number[] = [0]; // First tier always has baseAmount = 0

  for (let i = 1; i < tiers.length; i++) {
    const prevTier = tiers[i - 1];
    const prevPrevMaxPrice = i >= 2 ? tiers[i - 2].maxPrice : 0;
    const prevMaxPrice = prevTier.maxPrice === Infinity ? 0 : prevTier.maxPrice;
    const prevRate = prevTier.rate;

    // Calculate: previous baseAmount + (prevMaxPrice - prevPrevMaxPrice) * prevRate
    const taxableAmount = prevMaxPrice - prevPrevMaxPrice;
    const additionalTax = taxableAmount * prevRate;
    const newBaseAmount = baseAmounts[i - 1] + additionalTax;

    baseAmounts.push(Math.round(newBaseAmount * 100) / 100); // Round to 2 decimal places
  }

  return baseAmounts;
};

const BSDSection: React.FC<BSDSectionProps> = ({ control }) => {
  const {
    fields: bsdFields,
    append: bsdAppend,
    remove: bsdRemove,
    update: bsdUpdate,
  } = useFieldArray({
    control,
    name: "bsdTiers",
  });

  // Watch all bsdTiers to recalculate baseAmounts when maxPrice or rate changes
  const watchedTiers = useWatch({
    control,
    name: "bsdTiers",
  });

  // Auto-calculate and update baseAmounts when tiers change
  const recalculateBaseAmounts = useCallback(() => {
    if (!watchedTiers || watchedTiers.length === 0) return;

    const calculatedBaseAmounts = calculateBaseAmounts(watchedTiers);

    // Update each tier's baseAmount if it differs from calculated value
    watchedTiers.forEach((tier, index) => {
      const calculatedAmount = calculatedBaseAmounts[index];
      if (tier.baseAmount !== calculatedAmount) {
        bsdUpdate(index, { ...tier, baseAmount: calculatedAmount });
      }
    });
  }, [watchedTiers, bsdUpdate]);

  // Recalculate when maxPrice or rate changes
  useEffect(() => {
    recalculateBaseAmounts();
  }, [
    // Only trigger on maxPrice and rate changes, not baseAmount to avoid infinite loop
    JSON.stringify(
      watchedTiers?.map((t) => ({ maxPrice: t.maxPrice, rate: t.rate })),
    ),
  ]);

  const handleAddTier = () => {
    const lastTier = watchedTiers?.[watchedTiers.length - 1];
    const newMaxPrice =
      lastTier?.maxPrice === Infinity ? 0 : (lastTier?.maxPrice || 0) + 100000;
    bsdAppend({
      maxPrice: newMaxPrice,
      rate: 0.01,
      baseAmount: 0, // Will be auto-calculated
    });
  };

  return (
    <Box>
      <div className="pb-6 md:p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold">Buyer Stamp Duty (BSD) Rates</h3>
        <Text className="text-sm text-gray-600 mt-1">
          Configure BSD tiered rates based on property price ranges
        </Text>
      </div>
      <div className="pt-6 md:p-6 space-y-4">
        {bsdFields.map((field, index) => (
          <div key={field.id} className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <Text className="font-medium">Tier {index + 1}</Text>
              {bsdFields.length > 1 && (
                <Button
                  variant="text"
                  color="danger"
                  size="sm"
                  onClick={() => bsdRemove(index)}
                >
                  <PiTrashBold className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Controller
                name={`bsdTiers.${index}.maxPrice`}
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <div>
                    <Text className="text-sm font-medium mb-1">
                      Max Price ($)
                    </Text>
                    <Input
                      type="text"
                      {...field}
                      value={
                        value === Infinity
                          ? "Infinity"
                          : value
                            ? Number(value).toLocaleString("en-US")
                            : ""
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        const numericValue = val.replace(/,/g, "");
                        if (
                          !isNaN(Number(numericValue)) &&
                          numericValue !== ""
                        ) {
                          onChange(Number(numericValue));
                        } else if (numericValue === "") {
                          onChange(0);
                        }
                      }}
                      disabled={value === Infinity}
                      className={
                        value === Infinity
                          ? "bg-gray-100 cursor-not-allowed"
                          : ""
                      }
                      placeholder="0"
                    />
                  </div>
                )}
              />
              <Controller
                name={`bsdTiers.${index}.rate`}
                control={control}
                render={({ field }) => (
                  <div>
                    <Text className="text-sm font-medium mb-1">Rate (%)</Text>
                    <Input
                      type="number"
                      step="0.01"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      placeholder="0.01"
                    />
                  </div>
                )}
              />
              <Controller
                name={`bsdTiers.${index}.baseAmount`}
                control={control}
                render={({ field: { value } }) => (
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <Text className="text-sm font-medium">Base Amount</Text>
                      <Tooltip
                        content={
                          <div className="max-w-xs text-xs">
                            <p className="font-medium mb-1">Auto-calculated</p>
                            <p>
                              Cumulative tax from previous tiers. Formula:
                              BaseAmount[n] = BaseAmount[n-1] + (MaxPrice[n-1] -
                              MaxPrice[n-2]) × Rate[n-1]
                            </p>
                          </div>
                        }
                        placement="top"
                      >
                        <span className="cursor-help text-gray-400 hover:text-gray-600">
                          <PiInfoBold className="h-3.5 w-3.5" />
                        </span>
                      </Tooltip>
                    </div>
                    <Input
                      type="text"
                      value={
                        value ? Number(value).toLocaleString("en-US") : "0"
                      }
                      disabled
                      className="bg-gray-50 cursor-not-allowed text-gray-600"
                      placeholder="0"
                    />
                  </div>
                )}
              />
            </div>
          </div>
        ))}

        {/* Add Tier Button */}
        <Button
          variant="outline"
          className="w-full border-dashed"
          onClick={handleAddTier}
        >
          <PiPlusBold className="h-4 w-4 mr-2" />
          Add Tier
        </Button>
      </div>
    </Box>
  );
};

export default BSDSection;
